import React from 'react';

/**
 * Button — the single canonical button primitive.
 *
 * Variants:
 *   'primary'   — deep navy fill, white text (default action)
 *   'accent'    — SAID teal fill (highlighted primary/CTA)
 *   'secondary' — hairline outline, ink text (secondary action)
 *   'ghost'     — no border, ink text (tertiary / inline)
 *   'danger'    — clinical red (destructive)
 *
 * Sizes: 'sm' | 'md' | 'lg'  (default md)
 */

const BASE = {
  fontFamily: 'var(--diis-font-sans)',
  fontWeight: 500,
  letterSpacing: '0.005em',
  border: '1px solid transparent',
  borderRadius: 'var(--diis-radius)',
  cursor: 'pointer',
  transition: 'background var(--diis-motion), color var(--diis-motion), border-color var(--diis-motion), box-shadow var(--diis-motion)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  whiteSpace: 'nowrap',
  outline: 'none',
};

const SIZES = {
  sm: { padding: '6px 12px',  fontSize: '13px', height: '32px' },
  md: { padding: '9px 16px',  fontSize: '14px', height: '40px' },
  lg: { padding: '12px 22px', fontSize: '15px', height: '48px' },
};

const VARIANTS = {
  primary: {
    background: 'var(--diis-ink)',
    color: 'var(--diis-surface)',
    hoverBg: '#1B2E42',
    activeBg: '#08131E',
  },
  accent: {
    background: 'var(--diis-accent)',
    color: 'var(--diis-surface)',
    hoverBg: 'var(--diis-accent-hover)',
    activeBg: '#123E4C',
  },
  secondary: {
    background: 'var(--diis-surface)',
    color: 'var(--diis-ink)',
    border: '1px solid var(--diis-hairline-strong)',
    hoverBg: 'var(--diis-canvas)',
    activeBg: 'var(--diis-canvas-alt)',
  },
  ghost: {
    background: 'transparent',
    color: 'var(--diis-ink-muted)',
    hoverBg: 'var(--diis-canvas-alt)',
    activeBg: 'var(--diis-hairline)',
  },
  danger: {
    background: 'var(--diis-danger)',
    color: 'var(--diis-surface)',
    hoverBg: '#7B2323',
    activeBg: '#6A1E1E',
  },
};

export function Button({
  variant = 'primary',
  size = 'md',
  disabled = false,
  fullWidth = false,
  leadingIcon = null,
  trailingIcon = null,
  children,
  style,
  ...rest
}) {
  const [hovered, setHovered] = React.useState(false);
  const [pressed, setPressed] = React.useState(false);
  const v = VARIANTS[variant] || VARIANTS.primary;
  const s = SIZES[size] || SIZES.md;

  const bg = pressed
    ? (v.activeBg || v.hoverBg || v.background)
    : hovered
    ? (v.hoverBg || v.background)
    : v.background;

  return (
    <button
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      disabled={disabled}
      style={{
        ...BASE,
        ...s,
        background: bg,
        color: v.color,
        border: v.border || BASE.border,
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        width: fullWidth ? '100%' : undefined,
        boxShadow: pressed ? 'none' : hovered ? 'var(--diis-shadow-1)' : 'none',
        ...style,
      }}
      onFocus={(e) => (e.currentTarget.style.boxShadow = 'var(--diis-shadow-focus)')}
      onBlur={(e) => (e.currentTarget.style.boxShadow = 'none')}
      {...rest}
    >
      {leadingIcon && <span style={{ display: 'inline-flex' }}>{leadingIcon}</span>}
      <span>{children}</span>
      {trailingIcon && <span style={{ display: 'inline-flex' }}>{trailingIcon}</span>}
    </button>
  );
}
